import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  buildSessionFromLoginPayload,
  clearAuthSession,
  extractAccessToken,
  parseStoredSession,
  saveAuthSession,
  USER_ROLE,
} from "../utils/auth_storage";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [accessToken, setAccessToken] = useState(
    () => parseStoredSession().accessToken
  );
  const [session, setSession] = useState(() => parseStoredSession().session);

  const refreshSession = useCallback(() => {
    const stored = parseStoredSession();
    setAccessToken(stored.accessToken);
    setSession(stored.session);
  }, []);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (
        event.key === "accessToken" ||
        event.key === "userData" ||
        event.key === "userRole"
      ) {
        refreshSession();
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [refreshSession]);

  const login = useCallback((loginPayload, role) => {
    const token = extractAccessToken(loginPayload);
    const nextSession = buildSessionFromLoginPayload(loginPayload, role);
    if (!nextSession?.userId) {
      return null;
    }
    saveAuthSession(nextSession, token);
    setAccessToken(token);
    setSession(nextSession);
    return nextSession;
  }, []);

  const logout = useCallback(() => {
    clearAuthSession();
    setAccessToken(null);
    setSession(null);
  }, []);

  const value = useMemo(
    () => ({
      accessToken,
      session,
      isAuthenticated: Boolean(accessToken || session?.userId),
      isDriver: session?.role === USER_ROLE.DRIVER,
      isRider: session?.role === USER_ROLE.RIDER,
      login,
      logout,
      refreshSession,
    }),
    [accessToken, session, login, logout, refreshSession]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}

export { USER_ROLE };
